/**
 * @file   src\Pages\AddNewUser.tsx
 * @brief  Add New User page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import {
    React,
    Container,
    Breadcrumb,
    Button,
    Row,
    Col,
    useOutletContext,
    useNavigate,
    useEffect,
    useState,
    useParams,
    useRef,
  } from '../utils/thirdpartyComponents';
  import CWInput from '../components/CWInput';
  import CWPasswordInput from '../components/CWPasswordInput';
  import CWRadio from '../components/CWRadio';
  import { AppContextType } from '../interfaces/generalInterface';
  import { pageURLs } from '../utils/pageUrls';
  import * as Yup from 'yup';
  import { MessageToaster } from '../utils/toastUtil';
  import { addadminuserSchema } from '../validations/addadminuserSchema';
  import {
    EMAIL_MAX_LENGTH,
    NAME_MAX_LENGTH,
    PASSWORD_MAX_LENGTH,
  } from '../utils/constants';
  import axios from '../config/axiosConfig';
  import { SubAdminApi } from '../utils/apiUrls';
import SmallLoader from '../components/SmallLoader';
  
  const AddNewUser = (props: { edit: boolean; }) => {
    const navigate = useNavigate();
    const param =   useParams();
    const current_user_id = param.id ? parseInt(param.id) : 0;
  
    const toaster = new MessageToaster();
  
    // Access the context
    const { updateData } = useOutletContext<AppContextType>();
  
    // Initialize states for form data and errors
    const [formData, setFormData] = useState({
      permissionType: 1,
      name: '',
      email: '',
      password: '',
      repassword: '',
      userGuid:''
    });
  
    const [errors, setErrors] = useState({
      permissionType: 1,
      name: '',
      email: '',
      password: '',
      repassword: '',
    });
  
    const [loading, setLoading] = useState(false);
  
    const refs = useRef<{ [key: string]: HTMLInputElement | HTMLTextAreaElement | null }>({});
  
    useEffect(() => {
      updateData({
        heading: current_user_id ? 'Edit User' : 'Add New User',
        url: `/superadmin/${pageURLs.admin_users}`,
        showBackButton: true,
        showMainMenu: true,
        activeLink: 'admin-users',
      });

    }, [current_user_id, updateData]);
    
    useEffect(() => {
      
    if(current_user_id !=0  && props.edit==true)
      {
        getAdminDetails()
      }
    }, []);
  
    const onChangePermission = (value: number) => {
      setFormData((prevFormData) => ({ ...prevFormData, permissionType: value }));
    };
  
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
  
      setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
      setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
    };
  


    const getAdminDetails = async () => {
      const userId = current_user_id;
      if(userId >0)
      {
          try {
              setLoading(true); // Show loader
              let param = '';
              param += userId;
              const { data } =  await axios.get(SubAdminApi.VIEW_ADMIN_DETAILS + param)
              setFormData({
                  ...formData,
                  name: data?.data?.fullName,
                  email: data?.data?.email,
                  permissionType: data?.data?.permissionType ,
                  userGuid: data?.data?.userGUID,
              });
              
          } catch (error: any) {
              throw new Error(error.response?.data?.message || error.message);
          } finally {
              setLoading(false); // Hide loader
          }
         
      }
  }


    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
  
      try {
        if (props.edit) {
          // Handle edit user submission
          const AdminUpdate = await axios.post(SubAdminApi.EDIT_ADMIN_USER, {
              name: formData.name,
              permissionType: formData.permissionType,
              userGuid: formData.userGuid
              });
          setLoading(false)
          if (AdminUpdate?.data?.error === 0) {
             
              
              toaster.toastSuccess(AdminUpdate?.data?.message);
              
          }
          else {
              toaster.toastError(AdminUpdate?.data?.message);
          }
          navigate("/superadmin/" + pageURLs.admin_users);
      } else {
        await addadminuserSchema.validate(formData, { abortEarly: false });
  
        setLoading(true);
        try {
          const response = await axios.post(SubAdminApi.ADD_ADMIN_USER, formData);
  
          if (response?.data?.error === 0) {
            toaster.toastSuccess('User added successfully.');
            navigate(`/superadmin/${pageURLs.admin_users}`);
          } else {
            toaster.toastError(response?.data?.message || 'Failed to add user.');
          }
        } catch  {
          toaster.toastError('Failed to add user.');
        } finally {
          setLoading(false);
        }
      }
      } catch (validationError) {
        if (validationError instanceof Yup.ValidationError) {
          const validationErrors: Record<string, string> = {};
          validationError.inner.forEach((err) => {
            if (err.path) validationErrors[err.path] = err.message;
          });
          setErrors((prevErrors) => ({ ...prevErrors, ...validationErrors }));
        }
      }
    };
  
    return (
      <div className="hardware-listing-container addnew-item-wrap">
        <Container>
          <div className="nav-btn-title-sec d-flex justify-content-between align-items-center">
            <Breadcrumb>
              <Breadcrumb.Item
                onClick={() => navigate(`/superadmin/${pageURLs.admin_users}`)}
              >
                Admin Users
              </Breadcrumb.Item>
              <Breadcrumb.Item active>{current_user_id ? 'Edit User' : 'Add New User'}</Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="hardware-list-wrap">
              <div className="form-inner mx-auto">
                <div>
                  <h5 className="title f-bold">Permission</h5>
                  <div className="radio-field-group d-flex">
                    <CWRadio
                      id="fullpermission"
                      name="permission"
                      label="Full Permission"
                      checked={formData.permissionType == 1}
                      onChange={() => onChangePermission(1)}
                    />
                    <CWRadio
                      id="washmanagement"
                      name="permission"
                      label="Car Wash Management"
                      checked={formData.permissionType ==2}
                      onChange={() => onChangePermission(2)}
                    />
                  </div>
                </div>
                <h5 className="title f-bold">Personal Details</h5>
                <Row>
                  <Col md={12}>
                    <CWInput
                      id="name"
                      name="name"
                      type="text"
                      label="Name"
                      value={formData.name}
                      onChange={handleChange}
                      errorMessage={errors.name}
                      maxLength={NAME_MAX_LENGTH}
                      ref={(el) => (refs.current.name = el)}
                      isMandatory
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <CWInput
                      id="emailaddress"
                      name="email"
                      type="text"
                      label="Email Address"
                      value={formData.email}
                      onChange={handleChange}
                      errorMessage={errors.email}
                      maxLength={EMAIL_MAX_LENGTH}
                      ref={(el) => (refs.current.emailaddress = el)}
                      readOnly={props.edit}
                      autoComplete="off"
                      isMandatory
                    />
                  </Col>
                  {!props.edit && <Col md={6}>
                    <CWPasswordInput
                      id="password"
                      name="password"
                      label="Password"
                      type={'password'}
                      showpass={<span />}
                      value={formData.password}
                      onChange={handleChange}
                      errorMessage={errors.password}
                      maxLength={PASSWORD_MAX_LENGTH}
                      autoComplete="off"
                      isMandatory
                    />
                  </Col>}
                </Row>
                {!props.edit &&  <Row>
                  <Col md={6}>
                    <CWPasswordInput
                      id="confirmrepassword"
                      name="repassword"
                      label="Re-Type Password"
                      type={'password'}
                      value={formData.repassword}
                      onChange={handleChange}
                      errorMessage={errors.repassword}
                      maxLength={PASSWORD_MAX_LENGTH}
                      isMandatory
                      showpass={<span />}
                    />
                  </Col>
                </Row>}
              </div>
            </div>
            <div className="btn-sec d-flex justify-content-end">
              <Button
                title="Cancel"
                variant="primary"
                className="secondary-btn"
                onClick={() => navigate(`/superadmin/${pageURLs.admin_users}`)}
              >
                Cancel
              </Button>
              <Button
                title={current_user_id ? 'Update' : 'Save'}
                variant="primary"
                className="primary-btn"
                type="submit"
              >
                {current_user_id ? 'Update' : 'Save'}
              </Button>
            </div>
          </form>
        </Container>
        {loading && <SmallLoader />}
      </div>
      
    );
  };
  
  export default AddNewUser;
  